/* eslint-disable max-lines */
import React from 'react';
import { Col, Label, Row, Card, CardBody, CardHeader, InputGroup, InputGroupText } from 'reactstrap';
import NumberFormat from 'react-number-format';

import { selectAllOnFocus } from '../../../../helpers/selectOnFocus';
interface Schedule2RowData {
  numChildren: string;
  estimatedDeduction: string;
}

interface NC4StepFourFormData {
  sch1_homeMortgageInterest: string;
  sch1_realEstatePropertyTaxes: string;
  sch1_charitableContributions: string;
  sch1_medicalAndDentalExpenses: string;
  sch1_repaymentOfClaimOfRightIncome: string;
  sch1_totalEstimatedItemizedDeductions: string;

  sch2_single_income_0_20000: Schedule2RowData;
  sch2_single_income_20001_30000: Schedule2RowData;
  sch2_single_income_30001_40000: Schedule2RowData;
  sch2_single_income_40001_50000: Schedule2RowData;
  sch2_single_income_50001_60000: Schedule2RowData;
  sch2_single_income_60001_70000: Schedule2RowData;
  sch2_single_income_over_70000: Schedule2RowData;

  sch2_mfj_ss_income_0_40000: Schedule2RowData;
  sch2_mfj_ss_income_40001_60000: Schedule2RowData;
  sch2_mfj_ss_income_60001_80000: Schedule2RowData;
  sch2_mfj_ss_income_80001_100000: Schedule2RowData;
  sch2_mfj_ss_income_100001_120000: Schedule2RowData;
  sch2_mfj_ss_income_120001_140000: Schedule2RowData;
  sch2_mfj_ss_income_over_140000: Schedule2RowData;

  sch2_hoh_income_0_30000: Schedule2RowData;
  sch2_hoh_income_30001_45000: Schedule2RowData;
  sch2_hoh_income_45001_60000: Schedule2RowData;
  sch2_hoh_income_60001_75000: Schedule2RowData;
  sch2_hoh_income_75001_90000: Schedule2RowData;
  sch2_hoh_income_90001_105000: Schedule2RowData;
  sch2_hoh_income_over_105000: Schedule2RowData;

  sch2_mfs_income_0_20000: Schedule2RowData;
  sch2_mfs_income_20001_30000: Schedule2RowData;
  sch2_mfs_income_30001_40000: Schedule2RowData;
  sch2_mfs_income_40001_50000: Schedule2RowData;
  sch2_mfs_income_50001_60000: Schedule2RowData;
  sch2_mfs_income_60001_70000: Schedule2RowData;
  sch2_mfs_income_over_70000: Schedule2RowData;

  sch2_totalEstimatedChildDeduction: string;
  [key: string]: any;
}

interface NC4StepFourProps {
  formData: NC4StepFourFormData;
  handleNumericInputChange: (name: string, subFieldName?: keyof Schedule2RowData) => (values: any) => void;
}

const NC4StepFour: React.FC<NC4StepFourProps> = ({ formData, handleNumericInputChange }) => {
  const renderSchedule2Row = (
    incomeRange: string,
    deductionPerChild: string,
    fieldNamePrefix: keyof NC4StepFourFormData,
    numChildrenValue: string | undefined,
    estimatedDeductionValue: string | undefined,
  ) => (
    <Row className="mb-1 align-items-center no-gutters">
      <Col md="5" xs="5" className="pr-1">
        <small>{incomeRange}</small>
      </Col>
      <Col md="2" xs="2" className="px-1">
        <NumberFormat
          {...selectAllOnFocus}
          id={`${String(fieldNamePrefix)}_numChildren`}
          name={`${String(fieldNamePrefix)}.numChildren`}
          className="form-control form-control-sm text-center"
          value={numChildrenValue === '' ? undefined : numChildrenValue}
          onValueChange={handleNumericInputChange(String(fieldNamePrefix), 'numChildren')}
          thousandSeparator={false}
          decimalScale={0}
          allowNegative={false}
        />
      </Col>
      <Col md="2" xs="2" className="text-center px-1">
        <small>{deductionPerChild}</small>
      </Col>
      <Col md="3" xs="3" className="pl-1">
        <InputGroup>
          <InputGroupText>$</InputGroupText>
          <NumberFormat
            {...selectAllOnFocus}
            id={`${String(fieldNamePrefix)}_estimatedDeduction`}
            name={`${String(fieldNamePrefix)}.estimatedDeduction`}
            className="form-control text-right"
            value={estimatedDeductionValue === '' ? undefined : estimatedDeductionValue}
            onValueChange={handleNumericInputChange(String(fieldNamePrefix), 'estimatedDeduction')}
            thousandSeparator={true}
            decimalScale={2}
            fixedDecimalScale={true}
            allowNegative={false}
          />
        </InputGroup>
      </Col>
    </Row>
  );

  return (
    <>
      <div className="text-center mb-4">
        <h4 className="mb-0" style={{ fontWeight: 'bold' }}>
          NC-4 Allowance Worksheet Schedules
        </h4>
        <p className="text-muted" style={{ fontSize: '0.8rem' }}>
          Important: If you cannot reasonably estimate the amount to enter in the schedules below, you should enter ZERO
          (0) on Form NC-4, Line 1.
        </p>
      </div>
      <Card className="mb-4">
        <CardHeader tag="h6" className="bg-light p-2 d-flex justify-content-between align-items-center">
          <span>Schedule 1</span>
          <span style={{ fontWeight: 'normal', fontSize: '0.9rem' }}>Estimated N.C. Itemized Deductions</span>
        </CardHeader>
        <CardBody className="p-3">
          <Row className="mb-2 align-items-center">
            <Col md="9" xs="8">
              <Label htmlFor="nc4_sch1_homeMortgageInterest" className="mb-0" style={{ fontSize: '0.9rem' }}>
                Home Mortgage Interest
              </Label>
            </Col>
            <Col md="3" xs="4">
              <InputGroup>
                <InputGroupText>$</InputGroupText>
                <NumberFormat
                  {...selectAllOnFocus}
                  id="nc4_sch1_homeMortgageInterest"
                  name="sch1_homeMortgageInterest"
                  className="form-control text-right"
                  value={formData.sch1_homeMortgageInterest === '' ? undefined : formData.sch1_homeMortgageInterest}
                  onValueChange={handleNumericInputChange('sch1_homeMortgageInterest')}
                  thousandSeparator={true}
                  decimalScale={2}
                  fixedDecimalScale={true}
                  allowNegative={false}
                  placeholder="0.00"
                />
              </InputGroup>
            </Col>
          </Row>
          <Row className="mb-2 align-items-center">
            <Col md="9" xs="8">
              <Label htmlFor="nc4_sch1_realEstatePropertyTaxes" className="mb-0" style={{ fontSize: '0.9rem' }}>
                Real Estate Property Taxes
              </Label>
            </Col>
            <Col md="3" xs="4">
              <InputGroup>
                <InputGroupText>$</InputGroupText>
                <NumberFormat
                  {...selectAllOnFocus}
                  id="nc4_sch1_realEstatePropertyTaxes"
                  name="sch1_realEstatePropertyTaxes"
                  className="form-control text-right"
                  value={
                    formData.sch1_realEstatePropertyTaxes === '' ? undefined : formData.sch1_realEstatePropertyTaxes
                  }
                  onValueChange={handleNumericInputChange('sch1_realEstatePropertyTaxes')}
                  thousandSeparator={true}
                  decimalScale={2}
                  fixedDecimalScale={true}
                  allowNegative={false}
                  placeholder="0.00"
                />
              </InputGroup>
            </Col>
          </Row>
          <Row className="mb-2 align-items-center">
            <Col md="9" xs="8">
              <Label htmlFor="nc4_sch1_charitableContributions" className="mb-0" style={{ fontSize: '0.9rem' }}>
                Charitable Contributions (Same as allowed for federal purposes)
              </Label>
            </Col>
            <Col md="3" xs="4">
              <InputGroup>
                <InputGroupText>$</InputGroupText>
                <NumberFormat
                  {...selectAllOnFocus}
                  id="nc4_sch1_charitableContributions"
                  name="sch1_charitableContributions"
                  className="form-control text-right"
                  value={
                    formData.sch1_charitableContributions === '' ? undefined : formData.sch1_charitableContributions
                  }
                  onValueChange={handleNumericInputChange('sch1_charitableContributions')}
                  thousandSeparator={true}
                  decimalScale={2}
                  fixedDecimalScale={true}
                  allowNegative={false}
                  placeholder="0.00"
                />
              </InputGroup>
            </Col>
          </Row>
          <Row className="mb-2 align-items-center">
            <Col md="9" xs="8">
              <Label htmlFor="nc4_sch1_medicalAndDentalExpenses" className="mb-0" style={{ fontSize: '0.9rem' }}>
                Medical and Dental Expenses (Same as allowed for federal purposes)
              </Label>
            </Col>
            <Col md="3" xs="4">
              <InputGroup>
                <InputGroupText>$</InputGroupText>
                <NumberFormat
                  {...selectAllOnFocus}
                  id="nc4_sch1_medicalAndDentalExpenses"
                  name="sch1_medicalAndDentalExpenses"
                  className="form-control text-right"
                  value={
                    formData.sch1_medicalAndDentalExpenses === '' ? undefined : formData.sch1_medicalAndDentalExpenses
                  }
                  onValueChange={handleNumericInputChange('sch1_medicalAndDentalExpenses')}
                  thousandSeparator={true}
                  decimalScale={2}
                  fixedDecimalScale={true}
                  allowNegative={false}
                  placeholder="0.00"
                />
              </InputGroup>
            </Col>
          </Row>
          <Row className="mb-2 align-items-center">
            <Col md="9" xs="8">
              <Label htmlFor="nc4_sch1_repaymentOfClaimOfRightIncome" className="mb-0" style={{ fontSize: '0.9rem' }}>
                Repayment of Claim of Right Income
              </Label>
            </Col>
            <Col md="3" xs="4">
              <InputGroup>
                <InputGroupText>$</InputGroupText>
                <NumberFormat
                  {...selectAllOnFocus}
                  id="nc4_sch1_repaymentOfClaimOfRightIncome"
                  name="sch1_repaymentOfClaimOfRightIncome"
                  className="form-control text-right"
                  value={
                    formData.sch1_repaymentOfClaimOfRightIncome === ''
                      ? undefined
                      : formData.sch1_repaymentOfClaimOfRightIncome
                  }
                  onValueChange={handleNumericInputChange('sch1_repaymentOfClaimOfRightIncome')}
                  thousandSeparator={true}
                  decimalScale={2}
                  fixedDecimalScale={true}
                  allowNegative={false}
                  placeholder="0.00"
                />
              </InputGroup>
            </Col>
          </Row>
          <Row className="mb-2 align-items-center mt-3 pt-2 border-top">
            <Col md="9" xs="8">
              <Label
                htmlFor="nc4_sch1_totalEstimatedItemizedDeductions"
                className="mb-0"
                style={{ fontSize: '0.9rem', fontWeight: 'bold' }}
              >
                Total Estimated N.C. Itemized Deductions. Enter on Page 2, Part II, Line 1<br />
                <small className="text-muted" style={{ fontWeight: 'normal' }}>
                  *The sum of your home mortgage interest and real estate property taxes may not exceed $20,000. For
                  married taxpayers, the $20,000 limitation applies to the combined total of home mortgage interest and
                  real estate property taxes claimed by both spouses, rather than to each spouse separately.
                </small>
              </Label>
            </Col>
            <Col md="3" xs="4">
              <InputGroup>
                <InputGroupText>$</InputGroupText>
                <NumberFormat
                  {...selectAllOnFocus}
                  id="nc4_sch1_totalEstimatedItemizedDeductions"
                  name="sch1_totalEstimatedItemizedDeductions"
                  className="form-control text-right"
                  value={
                    formData.sch1_totalEstimatedItemizedDeductions === ''
                      ? undefined
                      : formData.sch1_totalEstimatedItemizedDeductions
                  }
                  onValueChange={handleNumericInputChange('sch1_totalEstimatedItemizedDeductions')}
                  thousandSeparator={true}
                  decimalScale={2}
                  fixedDecimalScale={true}
                  allowNegative={false}
                  placeholder="0.00"
                />
              </InputGroup>
            </Col>
          </Row>
        </CardBody>
      </Card>

      <Card className="mb-4">
        <CardHeader tag="h6" className="bg-light p-2 d-flex justify-content-between align-items-center">
          <span>Schedule 2</span>
          <span style={{ fontWeight: 'normal', fontSize: '0.9rem' }}>Estimated N.C. Child Deduction Amount</span>
        </CardHeader>
        <CardBody className="p-3">
          <p style={{ fontSize: '0.85rem' }}>
            A taxpayer who is allowed a federal child tax credit under section 24 of the Internal Revenue Code is
            allowed a deduction for each qualifying child unless adjusted gross income exceeds the threshold amount
            shown below.
          </p>
          <p style={{ fontSize: '0.85rem' }}>
            The N.C. child deduction amount can be claimed only for a child who is under 17 years of age on the last day
            of the year.
          </p>
          <Row className="mt-3 mb-2 text-center font-weight-bold no-gutters">
            <Col md="5" xs="5">
              <small>Filing Status & Adjusted Gross Income</small>
            </Col>
            <Col md="2" xs="2">
              <small>No. of Children</small>
            </Col>
            <Col md="2" xs="2">
              <small>Deduction Amount per Qualifying Child</small>
            </Col>
            <Col md="3" xs="3">
              <small>Estimated Deduction</small>
            </Col>
          </Row>

          <h6 className="text-muted mt-2 mb-1 pl-1">
            <small>Single</small>
          </h6>
          {renderSchedule2Row(
            'Up to $20,000',
            '$ 3,000',
            'sch2_single_income_0_20000',
            formData.sch2_single_income_0_20000?.numChildren,
            formData.sch2_single_income_0_20000?.estimatedDeduction,
          )}
          {renderSchedule2Row(
            'Over $20,000 Up to $30,000',
            '$ 2,500',
            'sch2_single_income_20001_30000',
            formData.sch2_single_income_20001_30000?.numChildren,
            formData.sch2_single_income_20001_30000?.estimatedDeduction,
          )}
          {renderSchedule2Row(
            'Over $30,000 Up to $40,000',
            '$ 2,000',
            'sch2_single_income_30001_40000',
            formData.sch2_single_income_30001_40000?.numChildren,
            formData.sch2_single_income_30001_40000?.estimatedDeduction,
          )}
          {renderSchedule2Row(
            'Over $40,000 Up to $50,000',
            '$ 1,500',
            'sch2_single_income_40001_50000',
            formData.sch2_single_income_40001_50000?.numChildren,
            formData.sch2_single_income_40001_50000?.estimatedDeduction,
          )}
          {renderSchedule2Row(
            'Over $50,000 Up to $60,000',
            '$ 1,000',
            'sch2_single_income_50001_60000',
            formData.sch2_single_income_50001_60000?.numChildren,
            formData.sch2_single_income_50001_60000?.estimatedDeduction,
          )}
          {renderSchedule2Row(
            'Over $60,000 Up to $70,000',
            '$ 500',
            'sch2_single_income_60001_70000',
            formData.sch2_single_income_60001_70000?.numChildren,
            formData.sch2_single_income_60001_70000?.estimatedDeduction,
          )}
          {renderSchedule2Row(
            'Over $70,000',
            '$ -',
            'sch2_single_income_over_70000',
            formData.sch2_single_income_over_70000?.numChildren,
            formData.sch2_single_income_over_70000?.estimatedDeduction,
          )}

          <h6 className="text-muted mt-3 mb-1 pl-1">
            <small>MFJ or SS</small>
          </h6>
          {renderSchedule2Row(
            'Up to $40,000',
            '$ 3,000',
            'sch2_mfj_ss_income_0_40000',
            formData.sch2_mfj_ss_income_0_40000?.numChildren,
            formData.sch2_mfj_ss_income_0_40000?.estimatedDeduction,
          )}
          {renderSchedule2Row(
            'Over $40,000 Up to $60,000',
            '$ 2,500',
            'sch2_mfj_ss_income_40001_60000',
            formData.sch2_mfj_ss_income_40001_60000?.numChildren,
            formData.sch2_mfj_ss_income_40001_60000?.estimatedDeduction,
          )}
          {renderSchedule2Row(
            'Over $60,000 Up to $80,000',
            '$ 2,000',
            'sch2_mfj_ss_income_60001_80000',
            formData.sch2_mfj_ss_income_60001_80000?.numChildren,
            formData.sch2_mfj_ss_income_60001_80000?.estimatedDeduction,
          )}
          {renderSchedule2Row(
            'Over $80,000 Up to $100,000',
            '$ 1,500',
            'sch2_mfj_ss_income_80001_100000',
            formData.sch2_mfj_ss_income_80001_100000?.numChildren,
            formData.sch2_mfj_ss_income_80001_100000?.estimatedDeduction,
          )}
          {renderSchedule2Row(
            'Over $100,000 Up to $120,000',
            '$ 1,000',
            'sch2_mfj_ss_income_100001_120000',
            formData.sch2_mfj_ss_income_100001_120000?.numChildren,
            formData.sch2_mfj_ss_income_100001_120000?.estimatedDeduction,
          )}
          {renderSchedule2Row(
            'Over $120,000 Up to $140,000',
            '$ 500',
            'sch2_mfj_ss_income_120001_140000',
            formData.sch2_mfj_ss_income_120001_140000?.numChildren,
            formData.sch2_mfj_ss_income_120001_140000?.estimatedDeduction,
          )}
          {renderSchedule2Row(
            'Over $140,000',
            '$ -',
            'sch2_mfj_ss_income_over_140000',
            formData.sch2_mfj_ss_income_over_140000?.numChildren,
            formData.sch2_mfj_ss_income_over_140000?.estimatedDeduction,
          )}

          <h6 className="text-muted mt-3 mb-1 pl-1">
            <small>HOH</small>
          </h6>
          {renderSchedule2Row(
            'Up to $30,000',
            '$ 3,000',
            'sch2_hoh_income_0_30000',
            formData.sch2_hoh_income_0_30000?.numChildren,
            formData.sch2_hoh_income_0_30000?.estimatedDeduction,
          )}
          {renderSchedule2Row(
            'Over $30,000 Up to $45,000',
            '$ 2,500',
            'sch2_hoh_income_30001_45000',
            formData.sch2_hoh_income_30001_45000?.numChildren,
            formData.sch2_hoh_income_30001_45000?.estimatedDeduction,
          )}
          {renderSchedule2Row(
            'Over $45,000 Up to $60,000',
            '$ 2,000',
            'sch2_hoh_income_45001_60000',
            formData.sch2_hoh_income_45001_60000?.numChildren,
            formData.sch2_hoh_income_45001_60000?.estimatedDeduction,
          )}
          {renderSchedule2Row(
            'Over $60,000 Up to $75,000',
            '$ 1,500',
            'sch2_hoh_income_60001_75000',
            formData.sch2_hoh_income_60001_75000?.numChildren,
            formData.sch2_hoh_income_60001_75000?.estimatedDeduction,
          )}
          {renderSchedule2Row(
            'Over $75,000 Up to $90,000',
            '$ 1,000',
            'sch2_hoh_income_75001_90000',
            formData.sch2_hoh_income_75001_90000?.numChildren,
            formData.sch2_hoh_income_75001_90000?.estimatedDeduction,
          )}
          {renderSchedule2Row(
            'Over $90,000 Up to $105,000',
            '$ 500',
            'sch2_hoh_income_90001_105000',
            formData.sch2_hoh_income_90001_105000?.numChildren,
            formData.sch2_hoh_income_90001_105000?.estimatedDeduction,
          )}
          {renderSchedule2Row(
            'Over $105,000',
            '$ -',
            'sch2_hoh_income_over_105000',
            formData.sch2_hoh_income_over_105000?.numChildren,
            formData.sch2_hoh_income_over_105000?.estimatedDeduction,
          )}

          <h6 className="text-muted mt-3 mb-1 pl-1">
            <small>MFS</small>
          </h6>
          {renderSchedule2Row(
            'Up to $20,000',
            '$ 3,000',
            'sch2_mfs_income_0_20000',
            formData.sch2_mfs_income_0_20000?.numChildren,
            formData.sch2_mfs_income_0_20000?.estimatedDeduction,
          )}
          {renderSchedule2Row(
            'Over $20,000 Up to $30,000',
            '$ 2,500',
            'sch2_mfs_income_20001_30000',
            formData.sch2_mfs_income_20001_30000?.numChildren,
            formData.sch2_mfs_income_20001_30000?.estimatedDeduction,
          )}
          {renderSchedule2Row(
            'Over $30,000 Up to $40,000',
            '$ 2,000',
            'sch2_mfs_income_30001_40000',
            formData.sch2_mfs_income_30001_40000?.numChildren,
            formData.sch2_mfs_income_30001_40000?.estimatedDeduction,
          )}
          {renderSchedule2Row(
            'Over $40,000 Up to $50,000',
            '$ 1,500',
            'sch2_mfs_income_40001_50000',
            formData.sch2_mfs_income_40001_50000?.numChildren,
            formData.sch2_mfs_income_40001_50000?.estimatedDeduction,
          )}
          {renderSchedule2Row(
            'Over $50,000 Up to $60,000',
            '$ 1,000',
            'sch2_mfs_income_50001_60000',
            formData.sch2_mfs_income_50001_60000?.numChildren,
            formData.sch2_mfs_income_50001_60000?.estimatedDeduction,
          )}
          {renderSchedule2Row(
            'Over $60,000 Up to $70,000',
            '$ 500',
            'sch2_mfs_income_60001_70000',
            formData.sch2_mfs_income_60001_70000?.numChildren,
            formData.sch2_mfs_income_60001_70000?.estimatedDeduction,
          )}
          {renderSchedule2Row(
            'Over $70,000',
            '$ -',
            'sch2_mfs_income_over_70000',
            formData.sch2_mfs_income_over_70000?.numChildren,
            formData.sch2_mfs_income_over_70000?.estimatedDeduction,
          )}
        </CardBody>
      </Card>
    </>
  );
};

export default NC4StepFour;
